/**
 * Entitlements and price book (TMMAPP-0012) — `GET /v1/me/entitlements`.
 * Quote (TMMAPP-0025) — `GET /v1/me/entitlements/quote`.
 */
import type {
  AnnualPrice as WireAnnualPrice,
  AnnualQuote as WireAnnualQuote,
  BandView as WireBand,
  EntitlementItem as WireEntitlement,
  EntitlementSponsoredSeat as WireSeat,
  EntitlementsResponse,
  Prices as WirePrices,
  QuoteMeter as WireMeter,
  QuotePromotion as WireQuotePromotion,
  QuoteResponse as WireQuote,
} from './generated';
import { request } from './client';
import { personNameOrNull } from './personNames';
import type { Tier } from './me';

export type EntitlementKind = 'SUBSCRIBER' | 'GUEST_SEAT';
export type EntitlementStatus = 'ACTIVE' | 'ENDING' | 'ENDED';

export interface Entitlement {
  kind: EntitlementKind;
  status: EntitlementStatus;
  sponsorDisplayName: string | null;
  startsAt: string;
  endsAt: string | null;
}

export interface EntitlementSeat {
  userId: number;
  /** `null` when the seat holder has no display name (render with `personName`). */
  displayName: string | null;
  status: EntitlementStatus;
  endsAt: string | null;
}

export interface PriceBand {
  from: number;
  to: number | null;
  flatMinor: number;
  perUnitMinor: number;
}

export interface AnnualPriceOffer {
  amountMinor: number;
  monthlyEquivalentMinor: number;
  savingsBp: number;
}

export interface PriceBook {
  currency: string;
  retailOfferId: number;
  baseMinor: number;
  seatBands: PriceBand[];
  connectedAccountBands: PriceBand[];
  /** The AI wallet's terms (TMMAPI-0151, MM-D-131): the first purchase enables AI. */
  aiFirstPurchaseMinor: number;
  aiRechargeTriggerMinor: number;
  aiRechargeAmountMinor: number;
  aiMonthlyCreditMinor: number;
  trialDaysNewSubscriber: number;
  trialDaysGuestConversion: number;
  graceDaysAfterEnd: number;
  /** Present when annual is offered (0051). Absent → hide the interval toggle. */
  annual: AnnualPriceOffer | null;
}

export interface QuoteMeter {
  count: number;
  includedCount: number;
  chargedCount: number;
  amountMinor: number;
  /** Price of the next unit on this meter at the quoted count; null when the wire omits it. */
  nextUnitMinor: number | null;
  /**
   * List price of unit `includedCount + 1`, whatever the quoted count (TMMAPI-0142): before tax
   * and promotion, including any flat fee of the first paid band. Null when absent (no band
   * prices it, it's free, or it's past the cap). The "each additional" clauses read this only.
   */
  additionalUnitMinor: number | null;
}

export interface QuotePromotion {
  code: string;
  kind: 'OFFER' | 'TRIAL_DAYS' | 'PERCENT_OFF_VARIABLE' | 'AMOUNT_OFF_VARIABLE' | null;
  reason: string | null;
  redeemable: boolean;
  statements: number | null;
  trialDays: number | null;
}

export interface AnnualQuote {
  baseMinor: number;
  totalFirstYearMinor: number;
}

/** The served quote; every total is pre-tax (MM-D-139: tax is calculated at checkout). */
export interface EntitlementsQuote {
  currency: string;
  baseMinor: number;
  seats: QuoteMeter;
  connections: QuoteMeter;
  subtotalMinor: number;
  totalMonthlyMinor: number;
  annual: AnnualQuote | null;
  discountMinor: number | null;
  promotion: QuotePromotion | null;
  trialDays: number;
}

export interface Entitlements {
  tier: Tier;
  entitlements: Entitlement[];
  sponsoredSeats: EntitlementSeat[];
  prices: PriceBook | null;
}

const KINDS: ReadonlySet<string> = new Set(['SUBSCRIBER', 'GUEST_SEAT']);
const STATUSES: ReadonlySet<string> = new Set(['ACTIVE', 'ENDING', 'ENDED']);
const TIERS: ReadonlySet<string> = new Set(['SUBSCRIBER', 'GUEST', 'NONE']);
const PROMO_KINDS: ReadonlySet<string> = new Set([
  'OFFER',
  'TRIAL_DAYS',
  'PERCENT_OFF_VARIABLE',
  'AMOUNT_OFF_VARIABLE',
]);

/** A band; an absent or zero `to` is open-ended. */
export function normalizePriceBand(w: WireBand): PriceBand {
  return {
    from: w.from,
    to: w.to == null || w.to === 0 ? null : w.to,
    flatMinor: w.flatMinor,
    perUnitMinor: w.perUnitMinor,
  };
}

function normalizeAnnualPrice(w: WireAnnualPrice): AnnualPriceOffer {
  return {
    amountMinor: w.amountMinor,
    monthlyEquivalentMinor: w.monthlyEquivalentMinor,
    savingsBp: w.savingsBp,
  };
}

export function normalizePriceBook(w: WirePrices): PriceBook {
  return {
    currency: w.currency,
    retailOfferId: w.retailOfferId,
    baseMinor: w.baseMinor,
    seatBands: w.seatBands.map(normalizePriceBand),
    connectedAccountBands: w.connectedAccountBands.map(normalizePriceBand),
    aiFirstPurchaseMinor: w.aiFirstPurchaseMinor,
    aiRechargeTriggerMinor: w.aiRechargeTriggerMinor,
    aiRechargeAmountMinor: w.aiRechargeAmountMinor,
    aiMonthlyCreditMinor: w.aiMonthlyCreditMinor,
    trialDaysNewSubscriber: w.trialDaysNewSubscriber,
    trialDaysGuestConversion: w.trialDaysGuestConversion,
    graceDaysAfterEnd: w.graceDaysAfterEnd,
    annual: w.annual == null ? null : normalizeAnnualPrice(w.annual),
  };
}

function normalizeQuoteMeter(w: WireMeter): QuoteMeter {
  return {
    count: w.count,
    includedCount: w.includedCount,
    chargedCount: w.chargedCount,
    amountMinor: w.amountMinor,
    nextUnitMinor: w.nextUnitMinor ?? null,
    additionalUnitMinor: w.additionalUnitMinor ?? null,
  };
}

function normalizeQuotePromotion(w: WireQuotePromotion): QuotePromotion {
  return {
    code: w.code,
    kind: w.kind !== undefined && PROMO_KINDS.has(w.kind) ? w.kind : null,
    reason: w.reason ?? null,
    redeemable: w.redeemable,
    statements: w.statements ?? null,
    trialDays: w.trialDays ?? null,
  };
}

function normalizeAnnualQuote(w: WireAnnualQuote): AnnualQuote {
  return { baseMinor: w.baseMinor, totalFirstYearMinor: w.totalFirstYearMinor };
}

export function normalizeQuote(w: WireQuote): EntitlementsQuote {
  return {
    currency: w.currency,
    baseMinor: w.baseMinor,
    seats: normalizeQuoteMeter(w.seats),
    connections: normalizeQuoteMeter(w.connections),
    subtotalMinor: w.subtotalMinor,
    totalMonthlyMinor: w.totalMonthlyMinor,
    annual: w.annual == null ? null : normalizeAnnualQuote(w.annual),
    discountMinor: w.discountMinor ?? null,
    promotion: w.promotion == null ? null : normalizeQuotePromotion(w.promotion),
    trialDays: w.trialDays,
  };
}

/** An entitlement; null (dropped) for a kind or status this build doesn't know. */
function normalizeEntitlement(w: WireEntitlement): Entitlement | null {
  if (!KINDS.has(w.kind) || !STATUSES.has(w.status)) return null;
  return {
    kind: w.kind,
    status: w.status,
    sponsorDisplayName: w.sponsorDisplayName ?? null,
    startsAt: w.startsAt,
    endsAt: w.endsAt ?? null,
  };
}

/** A sponsored seat; null (dropped) for a status this build doesn't know. */
function normalizeSeat(w: WireSeat): EntitlementSeat | null {
  if (!STATUSES.has(w.status)) return null;
  return {
    userId: w.userId,
    displayName: personNameOrNull(w.displayName ?? null),
    status: w.status,
    endsAt: w.endsAt ?? null,
  };
}

export function normalizeEntitlements(w: EntitlementsResponse): Entitlements {
  return {
    tier: TIERS.has(w.tier) ? w.tier : 'NONE',
    entitlements: w.entitlements.flatMap((row) => {
      const item = normalizeEntitlement(row);
      return item ? [item] : [];
    }),
    sponsoredSeats: w.sponsoredSeats.flatMap((row) => {
      const item = normalizeSeat(row);
      return item ? [item] : [];
    }),
    prices: w.prices == null ? null : normalizePriceBook(w.prices),
  };
}

export const entitlementsApi = {
  async get(): Promise<Entitlements> {
    return normalizeEntitlements(await request<EntitlementsResponse>('GET', '/v1/me/entitlements'));
  },
  async quote(params: {
    seats: number;
    connections: number;
    interval?: 'MONTH' | 'YEAR';
    promotionCode?: string;
  }): Promise<EntitlementsQuote> {
    const promotionCode = params.promotionCode?.trim();
    const promo = promotionCode != null && promotionCode.length > 0 ? promotionCode : undefined;
    return normalizeQuote(
      await request<WireQuote>('GET', '/v1/me/entitlements/quote', {
        query: {
          seats: params.seats,
          connections: params.connections,
          ...(params.interval != null ? { interval: params.interval } : {}),
          ...(promo != null ? { promotionCode: promo } : {}),
        },
      }),
    );
  },
};
